import {noop} from '@benchling/js-utils';
import {Box, Text, createStyles} from '@mantine/core';
import assert from 'assert';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {MinimalSchemaTag, SchemaTag} from 'folder-items/FileConstants';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import TextAndUrlValueEditor from 'metadata/TextAndUrlValueEditor';
import {tryParse} from 'notebook/data-tables/DatetimeFields';
import MulticolorStripedCircle from 'samples/plate-maps/MulticolorStripedCircle';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  ParameterFieldType,
  PlateMap,
} from 'samples/plate-maps/PlateMapConstants';
import {getColor} from 'samples/plate-maps/PlateMapUtil';
import {
  applyParameterValueToSelected,
  getWellIndicesWithParameterValue,
} from 'samples/plate-maps/scratch-plates/ScratchPlateParameterLayerControls';
import getTestProps from 'test/lib/cypress/getTestProps';
import BaseEditableField from 'ui-library/BaseEditableField';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import DatetimeField from 'ui-library/inputs/DatetimeField';
import UnitAwareTagValueEditor from 'units/UnitAwareTagValueEditor';
import * as modals from 'util/Modals';

import {ParameterLayerData} from '../parameter-layers/machine/ParameterLayerMachine';

type ParameterRawValue = string | number;

const NEW_OPTION_SENTINEL = 'NEW_OPTION_SENTINEL';

function keyForRawValue(rawValue: ParameterRawValue): string {
  return String(rawValue);
}

/** Rejects null/undefined and empty-string values; a numeric 0 is valid. */
function isValidRawValue(value: ParameterRawValue | null): value is ParameterRawValue {
  return value != null && value !== '';
}

/** Extracts a planned value's raw value for the given field type, or null if unset for that type. */
function extractPlannedRawValue(
  tag: MinimalSchemaTag,
  fieldType: ParameterFieldType
): ParameterRawValue | null {
  switch (fieldType) {
    case ParameterFieldType.FLOAT:
      return tag.floatValue ?? null;
    case ParameterFieldType.DATETIME:
      return tag.datetimeValue ?? tag.value ?? null;
    case ParameterFieldType.TEXT:
    default:
      return tag.value ?? null;
  }
}

/**
 * Datetime value editor that keeps a local display string. DatetimeField fires onChange on every
 * keystroke (including invalid partials); only canonical values are propagated upward, so typing a
 * datetime isn't wiped mid-entry.
 */
function ParameterDatetimeEditor({
  pendingValue,
  onUpdatePendingValue,
}: {
  pendingValue: string | null;
  onUpdatePendingValue: (value: string | null) => void;
}): JSX.Element {
  const [displayValue, setDisplayValue] = useState<string | null>(pendingValue);
  useEffect(() => {
    setDisplayValue(pendingValue);
  }, [pendingValue]);

  return (
    <DatetimeField
      labelStyle={DatetimeField.LABEL_STYLES.HIDDEN}
      value={displayValue}
      onChange={(value) => {
        setDisplayValue(value);
        if (value === '') {
          onUpdatePendingValue(null);
          return;
        }
        const canonical = tryParse(value);
        // Store canonical ISO-8601 only once it parses; leave the prior value while still typing.
        if (canonical != null) {
          onUpdatePendingValue(canonical);
        }
      }}
    />
  );
}

/**
 * Numeric editor that tracks the raw typed string locally (seeded once from `initialValue` at
 * mount), mirroring NumberFieldAssignmentSettings's `{numericValue, stringValue}` pattern. Feeding
 * ControlledNumericInput a value re-derived from the parsed number (e.g. `String(numericValue)`)
 * fights the user mid-keystroke — e.g. typing "2." can parse to numericValue 2, and echoing back
 * "2" drops the trailing "." and confuses the next keystroke.
 */
function ParameterFloatEditor({
  initialValue,
  onChange,
  onKeyDown,
}: {
  initialValue: number | null;
  onChange: (value: number | null) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}): JSX.Element {
  const [stringValue, setStringValue] = useState<string>(initialValue != null ? String(initialValue) : '');

  return (
    <ControlledNumericInput
      value={stringValue}
      onChange={(e) => {
        setStringValue(e.target.value);
        onChange(e.target.numericValue);
      }}
      onKeyDown={onKeyDown}
      dataTestAttrs={{component: 'ParameterLayerSettings', element: 'input'}}
      focusOnMount={true}
    />
  );
}

/**
 * Value editor for a single label, dispatching to the field-type-appropriate input — mirrors the
 * editor widgets used by {@link TextFieldAssignmentSettings}/{@link NumberFieldAssignmentSettings}
 * for well schema fields (TEXT via PrimitiveValueEditor, FLOAT via ControlledNumericInput), plus a
 * datetime editor for the one field type those components don't support.
 */
function ParameterValueEditor({
  fieldType,
  value,
  onChange,
  onKeyDown,
}: {
  fieldType: ParameterFieldType;
  value: ParameterRawValue | null;
  onChange: (value: ParameterRawValue | null) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}): JSX.Element {
  if (fieldType === ParameterFieldType.DATETIME) {
    return (
      <ParameterDatetimeEditor
        pendingValue={typeof value === 'string' ? value : null}
        onUpdatePendingValue={onChange}
      />
    );
  }
  if (fieldType === ParameterFieldType.FLOAT) {
    return (
      <ParameterFloatEditor
        initialValue={typeof value === 'number' ? value : null}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
    );
  }
  // Reuses the exact same text editor as TextFieldAssignmentSettings (for well schema fields) so a
  // scratch-plate parameter's text input looks identical to an inventory-plate one. Parameters have
  // no URL concept, so itemLinkAllowed is false (matching how the legacy plate map already treats
  // DATA_PARAMETER attributes) and tagSchemaField is a placeholder — TextAndUrlValueEditor never
  // reads it, only `value`/`url`.
  return (
    <TextAndUrlValueEditor
      autoFocus={true}
      itemLinkAllowed={false}
      defaultValue={{value: typeof value === 'string' ? value : '', url: ''} as unknown as SchemaTag}
      onChange={(state) => {
        onChange(state.value);
      }}
      onKeyDown={onKeyDown}
      placeholder="Label"
    />
  );
}

/**
 * One reusable value "label" for the parameter: click to assign it to the selected wells, or edit/
 * delete it (unless it's a locked planned value). Mirrors {@link TextValueOption}/
 * {@link NumericValueOption} in the sibling assignment-settings files — same BaseEditableField/
 * icon/tooltip chrome, so a scratch-plate parameter looks identical to an inventory-plate one.
 */
function ParameterValueOption({
  rawValue,
  fieldType,
  color,
  unitSymbol,
  isPlannedValue,
  isDuplicateValue,
  deleteOption,
  saveOption,
  assignOptionValue,
}: {
  rawValue: ParameterRawValue;
  fieldType: ParameterFieldType;
  color: string | null;
  unitSymbol: string | null;
  isPlannedValue: boolean;
  isDuplicateValue: (candidate: ParameterRawValue) => boolean;
  deleteOption: () => void;
  saveOption: (value: ParameterRawValue) => void;
  assignOptionValue: () => void;
}): JSX.Element {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editValue, setEditValue] = useState<ParameterRawValue | null>(rawValue);
  const isDuplicate = editValue != null && editValue !== rawValue && isDuplicateValue(editValue);

  function onOptionClick(): void {
    if (!isEditing) {
      assignOptionValue();
    }
  }

  function onClickCancel(): void {
    setEditValue(rawValue);
    setIsEditing(false);
  }

  function onSaveChanges(): void {
    if (!isDuplicate && isValidRawValue(editValue)) {
      saveOption(editValue);
      setIsEditing(false);
    }
  }

  const editor = (
    <ParameterValueEditor
      fieldType={fieldType}
      value={editValue}
      onChange={setEditValue}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onSaveChanges();
        }
      }}
    />
  );

  const {classes} = useStyles();

  return (
    <div
      className={classes.valueOption}
      {...getActionButtonRoleProps({onPress: onOptionClick})}
      {...getTestProps({
        component: 'ParameterLayerSettings',
        element: 'option',
        key: keyForRawValue(rawValue),
      })}
      data-amp-safe={false}
    >
      <div className={classes.optionInner}>
        {color != null && (
          <div className={classes.colorCircle}>
            <MulticolorStripedCircle cx={6} cy={6} radius={6} colors={[color]} />
          </div>
        )}
        <BaseEditableField
          editor={
            unitSymbol != null ? (
              <UnitAwareTagValueEditor editorComponent={editor} unitSymbol={unitSymbol} />
            ) : (
              editor
            )
          }
          extraActionButtons={{
            viewMode: [
              <BenchlingIcon
                key="delete"
                aria-label="Delete"
                className={classes.deleteIcon}
                icon="delete"
                {...getActionButtonRoleProps({onPress: deleteOption, stopPropagation: true})}
              />,
            ],
            editMode: [],
          }}
          isBeingEdited={isEditing}
          isCompleteEnabled={!isDuplicate && isValidRawValue(editValue)}
          isWritable={!isPlannedValue}
          viewer={
            <Tooltip text="Planned value labels cannot be edited or removed" isDisabled={!isPlannedValue}>
              <div className={classes.optionName}>
                <span>{String(rawValue)}</span>
                {unitSymbol != null && (
                  <Text span={true} size="sm" c="gray.6" pl={2}>
                    {unitSymbol}
                  </Text>
                )}
              </div>
            </Tooltip>
          }
          onCancel={onClickCancel}
          onComplete={onSaveChanges}
          onEdit={(e) => {
            e.stopPropagation();
            setIsEditing(true);
          }}
          isFullWidth={true}
        />
      </div>
      {isDuplicate && (
        <Text
          size="xs"
          c="red.6"
          mt={4}
          ml={16}
          {...getTestProps({component: 'ParameterLayerSettings', element: 'duplicateLabelError'})}
        >{`Label ${editValue} already exists.`}</Text>
      )}
    </div>
  );
}

/** Inline form for adding a brand-new label, mirroring the NEW_OPTION_SENTINEL row in the sibling files. */
function AddParameterValueOption({
  fieldType,
  color,
  isDuplicateValue,
  unitSymbol,
  onSave,
  onCancel,
}: {
  fieldType: ParameterFieldType;
  color: string | null;
  unitSymbol: string | null;
  isDuplicateValue: (candidate: ParameterRawValue) => boolean;
  onSave: (value: ParameterRawValue) => void;
  onCancel: () => void;
}): JSX.Element {
  const [value, setValue] = useState<ParameterRawValue | null>(null);
  const isDuplicate = value != null && isDuplicateValue(value);

  function onSaveChanges(): void {
    if (!isDuplicate && isValidRawValue(value)) {
      onSave(value);
    }
  }

  const editor = (
    <ParameterValueEditor
      fieldType={fieldType}
      value={value}
      onChange={setValue}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onSaveChanges();
        }
      }}
    />
  );

  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();

  return (
    <div
      className={classes.valueOption}
      {...getTestProps({component: 'ParameterLayerSettings', element: 'addOption', key: NEW_OPTION_SENTINEL})}
      data-amp-safe={false}
    >
      <div className={classes.optionInner}>
        {color != null && (
          <div className={classes.colorCircle}>
            <MulticolorStripedCircle cx={6} cy={6} radius={6} colors={[color]} />
          </div>
        )}
        <BaseEditableField
          editor={
            unitSymbol != null ? (
              <UnitAwareTagValueEditor editorComponent={editor} unitSymbol={unitSymbol} />
            ) : (
              editor
            )
          }
          isBeingEdited={true}
          isCompleteEnabled={!isDuplicate && isValidRawValue(value)}
          isWritable={true}
          viewer={<></>}
          onCancel={onCancel}
          onComplete={onSaveChanges}
          onEdit={noop}
          isFullWidth={true}
        />
      </div>
      {isDuplicate && (
        <Text
          size="xs"
          c="red.6"
          mt={4}
          ml={16}
          {...getTestProps({component: 'ParameterLayerSettings', element: 'duplicateLabelError'})}
        >{`Label ${value} already exists.`}</Text>
      )}
    </div>
  );
}

export interface ParameterLayerSettingsProps {
  plateMap: PlateMap;
  onEditPlateMap: (plateMap: PlateMap) => void;
  activeLayer: ParameterLayerData;
  /** Planned values from the referenced procedure parameter; surfaced as locked, unremovable labels. */
  plannedValues: Array<MinimalSchemaTag>;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  /**
   * Same map (and setter) the plate-grid preview reads for well coloring on this attribute — shared
   * rather than locally computed so a TEXT label's color always matches the well color, mirroring how
   * TextFieldAssignmentSettings and its plate grid share one assignmentGroupsById. Always empty for
   * FLOAT/DATETIME parameters, which have no color concept.
   */
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  setAssignmentGroupsById: (groups: {[id: string]: AssignmentGroup<AssignmentGroupValue>}) => void;
}

/**
 * Settings panel for a procedure-parameter layer: select wells, then click a label to assign its
 * value to them. The label list is seeded from the parameter's planned values (locked, sorted first)
 * plus whatever values are already entered on the plate; users can add, edit, or remove additional
 * labels. Mirrors {@link TextFieldAssignmentSettings}/{@link NumberFieldAssignmentSettings}'s
 * assignment-group pattern for well schema fields, down to the shared BaseEditableField/icon/color
 * chrome, so a scratch-plate parameter looks the same as an inventory-plate one. TEXT labels get a
 * color swatch (mirroring TextFieldAssignmentSettings); FLOAT/DATETIME labels don't (mirroring
 * NumberFieldAssignmentSettings, which has no color concept).
 */
export default function ParameterLayerSettings({
  plateMap,
  onEditPlateMap,
  activeLayer,
  plannedValues,
  selectedWellIndices,
  deselectWells,
  assignmentGroupsById,
  setAssignmentGroupsById,
}: ParameterLayerSettingsProps): JSX.Element {
  assert(activeLayer != null, 'Expected active layer to be defined.');

  const [addingNewOption, setAddingNewOption] = useState<boolean>(false);
  const [valueMap, setValueMap] = useState<Record<string, ParameterRawValue>>({});

  useEffect(() => {
    const newValueMap: Record<string, ParameterRawValue> = {};
    for (const well of plateMap.wells) {
      const rawValue = well.parameterLayers?.[activeLayer.layerId];
      if (rawValue != null) {
        newValueMap[keyForRawValue(rawValue)] = rawValue;
      }
    }
    for (const tag of plannedValues) {
      const rawValue = extractPlannedRawValue(tag, activeLayer.fieldType);
      if (rawValue != null) {
        newValueMap[keyForRawValue(rawValue)] = rawValue;
      }
    }
    // Merge rather than replace, so manually-added-but-unassigned labels survive plate/planned-value
    // changes (mirrors NumberFieldAssignmentSettings).
    setValueMap((prev) => ({...prev, ...newValueMap}));
  }, [plateMap.wells, plannedValues, activeLayer.layerId, activeLayer.fieldType]);

  // Next color for a newly-added TEXT label, computed the same way TextFieldAssignmentSettings does
  // — via the shared getColor generator over the SAME assignmentGroupsById map the plate grid reads,
  // so a new label's color can't collide with one the grid already assigned. Irrelevant for
  // FLOAT/DATETIME, which never populate assignmentGroupsById (mirrors NumberFieldAssignmentSettings,
  // which has no color concept).
  const nextColor = getColor(assignmentGroupsById).next().value;

  const plannedValueKeys = new Set(
    plannedValues
      .map((tag) => extractPlannedRawValue(tag, activeLayer.fieldType))
      .filter((rawValue): rawValue is ParameterRawValue => rawValue != null)
      .map((rawValue) => keyForRawValue(rawValue))
  );

  function isDuplicateValue(candidate: ParameterRawValue): boolean {
    return Object.hasOwn(valueMap, keyForRawValue(candidate));
  }

  function assignValueToSelected(rawValue: ParameterRawValue): void {
    if (selectedWellIndices.size === 0) {
      return;
    }
    onEditPlateMap(
      applyParameterValueToSelected(plateMap, activeLayer.layerId, rawValue, selectedWellIndices)
    );
    deselectWells();
  }

  function addNewOption(rawValue: ParameterRawValue): void {
    setValueMap({...valueMap, [keyForRawValue(rawValue)]: rawValue});
    if (activeLayer.fieldType === ParameterFieldType.TEXT) {
      const groupId = keyForRawValue(rawValue);
      setAssignmentGroupsById({
        ...assignmentGroupsById,
        [groupId]: {id: groupId, color: nextColor, name: String(rawValue), value: String(rawValue)},
      });
    }
    assignValueToSelected(rawValue);
    setAddingNewOption(false);
  }

  function saveOptionChanges(oldRawValue: ParameterRawValue, newRawValue: ParameterRawValue): void {
    const wellIndices = getWellIndicesWithParameterValue(plateMap, activeLayer.layerId, oldRawValue);
    if (wellIndices.size > 0) {
      onEditPlateMap(applyParameterValueToSelected(plateMap, activeLayer.layerId, newRawValue, wellIndices));
    }
    setValueMap((prev) => {
      const next = {...prev};
      delete next[keyForRawValue(oldRawValue)];
      next[keyForRawValue(newRawValue)] = newRawValue;
      return next;
    });
    if (activeLayer.fieldType === ParameterFieldType.TEXT) {
      const oldGroupId = keyForRawValue(oldRawValue);
      const newGroupId = keyForRawValue(newRawValue);
      const {[oldGroupId]: existingGroup, ...remainingGroups} = assignmentGroupsById;
      setAssignmentGroupsById({
        ...remainingGroups,
        [newGroupId]: {
          id: newGroupId,
          color: existingGroup?.color ?? nextColor,
          name: String(newRawValue),
          value: String(newRawValue),
        },
      });
    }
  }

  async function deleteLabel(rawValue: ParameterRawValue): Promise<void> {
    const wellIndices = getWellIndicesWithParameterValue(plateMap, activeLayer.layerId, rawValue);
    if (wellIndices.size > 0) {
      const confirmed = await modals.confirm(
        `Remove label ${rawValue}`,
        `This label will be removed from ${wellIndices.size} wells. Are you sure you want to continue?`,
        ButtonStyle.NEGATIVE,
        {acceptText: 'Remove', rejectText: 'Cancel', enableRedesign: true, promiseCompatible: true}
      );
      if (!confirmed) {
        return;
      }
      onEditPlateMap(applyParameterValueToSelected(plateMap, activeLayer.layerId, null, wellIndices));
    }
    setValueMap((prev) => {
      const next = {...prev};
      delete next[keyForRawValue(rawValue)];
      return next;
    });
    if (activeLayer.fieldType === ParameterFieldType.TEXT) {
      const {[keyForRawValue(rawValue)]: _removed, ...remainingGroups} = assignmentGroupsById;
      setAssignmentGroupsById(remainingGroups);
    }
  }

  // Sort naturally/numerically, with planned values first.
  const sortedLabels = Object.values(valueMap).sort((a, b) => {
    const aPlanned = plannedValueKeys.has(keyForRawValue(a));
    const bPlanned = plannedValueKeys.has(keyForRawValue(b));
    if (aPlanned !== bPlanned) {
      return Number(bPlanned) - Number(aPlanned);
    }
    if (activeLayer.fieldType === ParameterFieldType.FLOAT) {
      return Number(a) - Number(b);
    }
    return String(a).localeCompare(String(b));
  });

  const unitSymbol = activeLayer.unitSymbol ?? null;

  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes: rootClasses} = useStyles();

  return (
    <div {...getTestProps({component: 'ParameterLayerSettings', element: 'root'})}>
      <Text fw={700} size="sm">
        {activeLayer.name}
      </Text>
      <Text size="sm" c="gray.7" mt={4}>
        Select wells in the plate, then select a label to apply to them
      </Text>
      <Box className={classNames(rootClasses.optionList, 'scrollbar-override')}>
        {sortedLabels.map((rawValue) => (
          <ParameterValueOption
            key={keyForRawValue(rawValue)}
            rawValue={rawValue}
            fieldType={activeLayer.fieldType}
            color={assignmentGroupsById[keyForRawValue(rawValue)]?.color ?? null}
            unitSymbol={unitSymbol}
            isPlannedValue={plannedValueKeys.has(keyForRawValue(rawValue))}
            isDuplicateValue={isDuplicateValue}
            assignOptionValue={() => {
              assignValueToSelected(rawValue);
            }}
            saveOption={(newValue) => {
              saveOptionChanges(rawValue, newValue);
            }}
            deleteOption={() => deleteLabel(rawValue)}
          />
        ))}
        {addingNewOption ? (
          <AddParameterValueOption
            fieldType={activeLayer.fieldType}
            color={activeLayer.fieldType === ParameterFieldType.TEXT ? nextColor : null}
            unitSymbol={unitSymbol}
            isDuplicateValue={isDuplicateValue}
            onSave={addNewOption}
            onCancel={() => {
              setAddingNewOption(false);
            }}
          />
        ) : (
          <Button
            buttonStyle={ButtonStyle.TEXT}
            size={ButtonSize.SMALL}
            icon="compose"
            text="Add label"
            disabled={false}
            onClick={() => {
              setAddingNewOption(true);
            }}
          />
        )}
      </Box>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  optionList: {
    marginTop: theme.spacing.sm,
    overflowY: 'auto',
  },
  valueOption: {
    cursor: 'pointer',
    width: '100%',
    padding: theme.spacing.xs,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  optionInner: {
    display: 'flex',
    alignItems: 'center',
  },
  colorCircle: {
    marginRight: theme.spacing.xs,
    display: 'inline-flex',
  },
  optionName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  deleteIcon: {
    color: theme.colors.gray[6],
  },
}));
